/**
 * Whether the weigh-in page opens today's entry with the skinfold (caliper) and waist (tape)
 * sections switched on, and which body-fat info card it shows beside them.
 *
 * The page follows the user's measuring rhythm (`getRecommendedBfFrequencyDays` in
 * `shared/src/body-comp-cadence.ts`): on a measuring day the sections the user last measured
 * are switched on; between measuring days they stay off and a countdown says when the next
 * one is. When the last progress check needs another body-fat reading to judge progress, the
 * sections come on between measuring days too (#6067). See
 * `design-docs/body-comp-measurement-cadence.md`.
 *
 * Kept free of Angular so a unit test can drive it (`edit-weigh-in-bf-sections.test.ts`).
 */

export interface BodyFatSectionsInputs {
  /** true when the user has logged body fat (skinfolds or waist) before */
  hasPreviousBfLog: boolean;
  /** whole days since the last body-fat entry */
  bfDaysAgo: number;
  /** the user's recommended measuring rhythm, in days */
  bfFrequencyDays: number;
  /** true when the last body-fat entry carried skinfold readings */
  lastBfLogHasCalipers: boolean;
  /** true when the last body-fat entry carried a waist reading */
  lastBfLogHasTape: boolean;
  /** true when the last progress check needs a body-fat reading before it can judge progress (#6067) */
  progressCheckNeedsBodyFat: boolean;
}

export interface BodyFatSections {
  calipersShown: boolean;
  waistShown: boolean;
  /** show the card for a user who has never measured body fat */
  showFirstTimeInfo: boolean;
  /** show the "you can measure again in N days" countdown */
  showCooldown: boolean;
  /** days until the next measuring day; meaningful only when showCooldown is true */
  cooldownDaysLeft: number;
  /** show the line saying the progress check needs a reading today (#6067) */
  showProgressCheckNeedsBodyFat: boolean;
}

export function decideBodyFatSections(inputs: BodyFatSectionsInputs): BodyFatSections {
  const sections: BodyFatSections = {
    calipersShown: false,
    waistShown: false,
    showFirstTimeInfo: false,
    showCooldown: false,
    cooldownDaysLeft: 0,
    showProgressCheckNeedsBodyFat: false,
  };

  // if the user has never measured body fat, show the first-time card; both sections stay off
  if (!inputs.hasPreviousBfLog) {
    sections.showFirstTimeInfo = true;
    return sections;
  }

  // if it is a measuring day, show what the user last used
  if (inputs.bfDaysAgo >= inputs.bfFrequencyDays) {
    sections.calipersShown = inputs.lastBfLogHasCalipers;
    sections.waistShown = inputs.lastBfLogHasTape;
    return sections;
  }

  // if the progress check cannot judge progress without another body-fat reading, ask for one
  // today: the rhythm alone can leave the check waiting for days (#6067)
  if (inputs.progressCheckNeedsBodyFat) {
    sections.calipersShown = inputs.lastBfLogHasCalipers;
    sections.waistShown = inputs.lastBfLogHasTape;
    sections.showProgressCheckNeedsBodyFat = true;
    return sections;
  }

  // otherwise the user is between measuring days: keep the sections off and count down
  sections.showCooldown = true;
  sections.cooldownDaysLeft = inputs.bfFrequencyDays - inputs.bfDaysAgo;
  return sections;
}
